import { createApp } from "vue";

import { translations } from "@/shared/localization/translations";
import { Ripple } from "@/shared/ui/Ripple";

import PopupContent from "@/components/dialog/PopupContent.vue";

export interface IPopupSettings {
    message?: string;
    title?: string;
    confirmHandler?: { () };
    confirmButton?: string;
    cancelButton?: string;
    uniqueId?: string;
    highlightConfirm?: boolean;
    highlightCancel?: boolean;
    confirmBtnOnTheLeft?: boolean;
    containerClass?: string;
    cancelHandler?: { () };
    showConfirmButton?: boolean;
    closePopupOnOverlayClick?: boolean;
    showCloseButton?: boolean;

    component?: any; // Vue component as content
    componentProps?: Record<string, any>; // Vue component as content props
    confirmButtonType?: ButtonType; // primary | danger
    cancelButtonType?: ButtonType;
    zIndex?: number;
}

export enum ButtonType {
    primary,
    danger,
}

export class Popup {
    public $container!: JQuery;
    public $dim!: JQuery;
    public $popup!: JQuery;
    private $btns!: JQuery;

    private static popupCounter = 0;
    private popupId: string;
    private popupApp?: any;

    private constructor(settings: IPopupSettings, $parent: JQuery) {
        this.popupId = `popup-${Popup.popupCounter++}`;
        this.initializePopup(settings, $parent);
    }

    private initializePopup(settings: IPopupSettings, $parent: JQuery) {
        this.$container = $('<div class="popup-container" />').appendTo($parent);
        
        if (settings.zIndex != null) {
            this.$container.css("z-index", settings.zIndex);   
        }
     
        if (settings.containerClass) {
            this.$container.addClass(settings.containerClass);
        }

        this.addDimOverlay(this.$container, $parent, settings.closePopupOnOverlayClick);

        // init popup with text
        this.$popup = $(`<div class="popup-window ${settings.uniqueId ?? ""}" data-popup-id="${this.popupId}"></div>`
        ).appendTo(this.$container);
        if (settings.title !== undefined) {
            $(`<div class="title">${settings.title}</div>`).appendTo(this.$popup);
        }

        const $content = $(`<div class="text"></div>`).appendTo(this.$popup);

        if (settings.component) {
            const targetClass = `popup-content-target-${this.popupId}`;
            $(`<div class="${targetClass}"></div>`).appendTo($content);

            this.popupApp = createApp(PopupContent, {
                component: settings.component,
                componentProps: settings.componentProps,
                targetClass: `.${targetClass}`, // pass unique selector
            });

            const mountPoint = $("<div />").appendTo($content)[0];
            this.popupApp.mount(mountPoint);
        } else {
            $content.html(settings.message ?? "");
        }

        if (settings.showCloseButton) {
            this.addCloseButton();
        }

        // init buttons
        const confirmLeftClass = settings.confirmBtnOnTheLeft ? "confirm-left" : "";

        const showConfirmButton = settings.showConfirmButton !== false;
        const showCancelButton = settings.cancelButton !== undefined;
        if (!showConfirmButton && !showCancelButton) return;

        this.$btns = $(`<div class="btns ${confirmLeftClass}"  />`).appendTo(this.$popup);

        if (showCancelButton) {
            this.addCancelButton(
                settings.highlightConfirm,
                settings.cancelButton,
                settings.highlightCancel,
                settings.cancelHandler
            );
        }
        if (showConfirmButton) {
            this.addConfirmButton(
                settings.confirmHandler,
                settings.confirmButton,
                settings.highlightConfirm,
                settings.confirmButtonType
            );
        }
    }

    private addDimOverlay($container: JQuery, $parent: JQuery, closePopupOnOverlayClick: boolean | undefined) {
        let $dim = $parent.find(".popup-overlay:not(.before-close)");
        if ($dim.length) {
            $dim.stop(true, false);
            this.$container.addClass("transparent-container");
        } else {
            this.$dim = $('<div class="popup-overlay" />').appendTo($container);
            $dim = this.$dim;
        }

        if (closePopupOnOverlayClick) {
            $dim.click(() => {
                if (this.$popup?.length) {
                    this.close();
                }
            });
        }
    }

    private addCancelButton(
        highlightConfirm?: boolean,
        cancelButton?: string,
        highlightCancel?: boolean,
        cancelHandler?: { () }
    ) {
        const btnClass = highlightCancel === true ? "orange" : highlightConfirm === true ? "" : "secondary";
        const btnText = cancelButton === undefined ? translations.general.cancel : cancelButton;

        const $btn = $(`<button type="button" class="popup-btn ${btnClass}">${btnText}</button>`);
        $btn.appendTo(this.$btns);

        Ripple.bind($btn);
        $btn.click(() => {
            this.close();

            if (cancelHandler) {
                cancelHandler();
            }
        });
    }

    private addConfirmButton(
        confirmHandler?: { () },
        confirmButton?: string,
        highlightConfirm?: boolean,
        buttonType?: ButtonType
    ) {
        let btnClass = highlightConfirm ? "orange" : "";
        if (buttonType != null) {
            switch (buttonType) {
                case ButtonType.primary:
                    btnClass = "primary";
                    break;
                case ButtonType.danger:
                    btnClass = "danger";
                    break;
            }
        }
        const btnText = confirmButton === undefined ? translations.general.ok : confirmButton;

        const $btn = $(`<button type="button" class="popup-btn confirm ${btnClass}">${btnText}</button>`);
        $btn.appendTo(this.$btns);

        Ripple.bind($btn);
        $btn.click(() => {
            this.close();

            if (confirmHandler) {
                confirmHandler();
            }
        });
    }

    private addCloseButton() {
        const $closeBtn = $(`<button type="button" class="popup-btn close"><i class="fal fa-times"></i></button>`);
        $closeBtn.prependTo(this.$popup);

        Ripple.bind($closeBtn);

        $closeBtn.click(() => {
            this.close();
        });
    }

    public close() {
        this.$popup.addClass("closing");

        this.$popup.delay(600).queue((e) => {
            if (this.popupApp) {
                this.popupApp.unmount();
            }
            this.$popup.remove();
            this.$container.remove();
            e();
        });

        //check if dim needs to be removed
        if (!this.$dim) return;

        this.$dim.addClass("before-close");

        const dim = this.$dim.delay(250).queue((c) => {
            dim.addClass("closing")
                .delay(350)
                .queue((a) => {
                    dim.remove();
                    a();
                });

            c();
        });
    }

    public static show(settings: IPopupSettings, parent = $(".app")) {
        // check if popup with same uniqueId is already shown
        if (Popup.uniqueIdPopupAlreadyExists(settings.uniqueId, parent)) {
            return null;
        }

        return new Popup(settings, parent);
    }

    private static uniqueIdPopupAlreadyExists(uniqueId: string | undefined, parent: JQuery) {
        return uniqueId !== undefined && parent.find(".popup-window." + uniqueId).length !== 0;
    }
}
